<template>
  <div>
    <v-form
      v-if="editMode"
      ref="form"
      v-model="isFormValid"
      lazy-validation
    >
      <div class="title mb-4">
        {{ sections.business }}
      </div>

      <v-text-field
        id="fullName"
        :label="formFields.fullName.label"
        v-model="formFields.fullName.value"
        :rules="formFields.fullName.rules"
      />

      <v-text-field
        id="phone"
        :label="formFields.phone.label"
        v-model="formFields.phone.value"
        :rules="formFields.phone.rules"
        persistent-placeholder
      />

      <v-text-field
        id="email"
        :label="formFields.email.label"
        v-model="formFields.email.value"
      />

      <v-textarea
        id="deliveryAddress"
        :label="formFields.deliveryAddress.label"
        v-model="formFields.deliveryAddress.value"
        :rules="formFields.deliveryAddress.rules"
        hint="Including postcode and country. We will use that for sending you our gifts!"
        persistent-hint
      />

      <div class="title mt-12 mb-4">
        {{ sections.documents }}
      </div>

      <v-file-input
        id="passport"
        :label="formFields.passport.label"
        v-model="formFields.passport.value"
        :rules="formFields.passport.rules"
        :clearable="false"
        :autofocus="false"
        :hint="supportedFilesHint"
        persistent-hint
      />
      <!-- @todo add supported files note 'pdf', 'doc', 'docx', 'png', 'jpg', 'jpeg' -->

      <div class="title mt-12 mb-4">
        {{ sections.company }}
      </div>

      <v-text-field
        id="supplierName"
        :label="formFields.supplierName.label"
        v-model="formFields.supplierName.value"
        :rules="formFields.supplierName.rules"
      />

      <v-textarea
        id="supplierAddress"
        :label="formFields.supplierAddress.label"
        v-model="formFields.supplierAddress.value"
        :rules="formFields.supplierAddress.rules"
        hint="Including postcode and country"
        persistent-hint
      />

      <v-text-field
        id="registrationNumber"
        :label="formFields.registrationNumber.label"
        v-model="formFields.registrationNumber.value"
        :rules="formFields.registrationNumber.rules"
      />

      <v-text-field
        id="vat"
        :label="formFields.vat.label"
        v-model="formFields.vat.value"
        :rules="formFields.vat.rules"
      />

      <v-file-input
        id="professionalLiabilityInsurance"
        :label="formFields.professionalLiabilityInsurance.label"
        v-model="formFields.professionalLiabilityInsurance.value"
        :rules="formFields.professionalLiabilityInsurance.rules"
        :clearable="false"
        :autofocus="false"
        :hint="supportedFilesHint"
        persistent-hint
      />

      <v-file-input
        id="publicLiabilityInsurance"
        :label="formFields.publicLiabilityInsurance.label"
        v-model="formFields.publicLiabilityInsurance.value"
        :rules="formFields.publicLiabilityInsurance.rules"
        :clearable="false"
        :autofocus="false"
        :hint="supportedFilesHint"
        persistent-hint
      />

      <div class="title mt-12">
        {{ sections.payment }}
      </div>
      <p class="mb-5 body-2 darkPurple--text">
        We require this information to be able to process your invoices. Only relevant parties within Riverflex will have access to this information
      </p>

      <v-text-field
        id="iban"
        :label="formFields.iban.label"
        v-model="formFields.iban.value"
        :rules="formFields.iban.rules"
      />


      <v-text-field
        id="swift"
        :label="formFields.swift.label"
        v-model="formFields.swift.value"
        :rules="formFields.swift.rules"
      />

      <div class="d-flex justify-end">
        <v-btn
          rounded
          color="primary"
          large
          light
          outlined
          :disabled="loading"
          @click="handleCancelClick"
        >
          Cancel
        </v-btn>
        <v-btn
          class="ml-4"
          rounded
          color="primary"
          large
          light
          :loading="loading"
          @click="handleSaveClick"
        >
          Submit form
        </v-btn>
      </div>
    </v-form>
    <div v-else>
      <div class="title">
        {{ sections.business }}
      </div>
      <BusinessDetailsField
        :label="formFields.fullName.label"
        :value="formFields.fullName.value"
      />
      <BusinessDetailsField
        :label="formFields.phone.label"
        :value="formFields.phone.value"
      />
      <BusinessDetailsField
        :label="formFields.email.label"
        :value="formFields.email.value"
      />
      <BusinessDetailsField
        :label="formFields.deliveryAddress.label"
        :value="formFields.deliveryAddress.value"
        style="min-height: 160px;"
      />

      <div class="title mt-12">
        {{ sections.documents }}
      </div>

      <BusinessDetailsField
        :label="formFields.passport.label"
        :value="initialValues.passport"
        type="file"
      />

      <div class="title mt-12">
        {{ sections.company }}
      </div>

      <BusinessDetailsField
        :label="formFields.supplierName.label"
        :value="formFields.supplierName.value"
      />
      <BusinessDetailsField
        :label="formFields.supplierAddress.label"
        :value="formFields.supplierAddress.value"
        style="min-height: 160px;"
      />
      <BusinessDetailsField
        :label="formFields.registrationNumber.label"
        :value="formFields.registrationNumber.value"
      />
      <BusinessDetailsField
        :label="formFields.vat.label"
        :value="formFields.vat.value"
      />
      <BusinessDetailsField
        :label="formFields.professionalLiabilityInsurance.label"
        :value="initialValues.professionalLiabilityInsurance"
        type="file"
      />
      <BusinessDetailsField
        :label="formFields.publicLiabilityInsurance.label"
        :value="initialValues.publicLiabilityInsurance"
        type="file"
      />

      <div class="title mt-12 mb-4">
        {{ sections.payment }}
      </div>

      <BusinessDetailsField
        :label="formFields.iban.label"
        :value="formFields.iban.value"
      />
      <BusinessDetailsField
        :label="formFields.swift.label"
        :value="formFields.swift.value"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { mapGetters } from 'vuex'
import isEmail from 'validator/es/lib/isEmail'
import isVAT from 'validator/es/lib/isVAT'
import isIBAN from 'validator/es/lib/isIBAN'
import isBIC from 'validator/es/lib/isBIC'

import { IRuleFunction } from '@/types/form'
import { requiredRule } from '@/validation/rules'
import { IUser } from '@/types/models/user.model'
import BusinessDetailsField from '@/components/businessDetails/BusinessDetailsField.vue'
import { getFileNameFromUrl } from '@/utils/vuex'

export interface IField {
  value: any
  rules?: IRuleFunction[]
  label?: string;
}
export type FieldId = 'fullName'|'phone'|'email'|'deliveryAddress'|'passport'|'supplierName'|'supplierAddress'|'registrationNumber'|'vat'|'professionalLiabilityInsurance'|'publicLiabilityInsurance'|'iban'|'swift';
export const fileFields = ['passport', 'professionalLiabilityInsurance', 'publicLiabilityInsurance']
export type FormValues = {
  fullName: string
  phone: string
  email: string
  deliveryAddress: string
  passport?: File
  supplierName: string
  supplierAddress: string
  registrationNumber: string
  vat: string
  professionalLiabilityInsurance?: File
  publicLiabilityInsurance?: File
  iban: string
  swift: string
}

export type InitialFormValues = Omit<FormValues, 'publicLiabilityInsurance' | 'passport' | 'professionalLiabilityInsurance'> & {
  passport?: string
  professionalLiabilityInsurance?: string
  publicLiabilityInsurance?: string
}

export const defaultFormValues = {
  fullName: undefined,
  phone: undefined,
  email: '',
  deliveryAddress: '',
  passport: undefined,
  supplierName: '',
  supplierAddress: '',
  registrationNumber: '',
  vat: '',
  professionalLiabilityInsurance: undefined,
  publicLiabilityInsurance: undefined,
  iban: '',
  swift: '',
}

@Component({
  components: {
    BusinessDetailsField,
  },
  computed: {
    ...mapGetters(['profile']),
  }
})
export default class BusinessDetailsForm extends Vue {
  @Prop() initialValues?: InitialFormValues;
  @Prop({ default: true }) editMode: boolean = true;
  @Prop({ default: false }) loading: boolean = false;

  $refs: {
    form: any
  }
  isFormValid: boolean = true;
  formFields: Record<FieldId, IField> = {
    fullName: {
      label: 'Full name',
      value: '',
      rules: [requiredRule],
    },
    phone: {
      label: 'Phone number',
      value: '',
      rules: [requiredRule]
    },
    email: {
      label: 'Business email',
      value: '',
      rules: [requiredRule, (value) => isEmail(value) || 'Email is not valid!']
    },
    deliveryAddress: {
      label: 'Personal delivery address',
      value: '',
      rules: [requiredRule],
    },
    passport: {
      label: 'Upload scan/photo of passport',
      value: undefined,
      rules: [requiredRule]
    },
    supplierName: {
      label: 'Supplier name',
      value: '',
      rules: [requiredRule]
    },
    supplierAddress: {
      label: 'Supplier address',
      value: '',
      rules: [requiredRule]
    },
    registrationNumber: {
      label: 'Company registration number',
      value: '',
      rules: [requiredRule]
    },
    vat: {
      label: 'VAT number',
      value: '',
      rules: [requiredRule/*, (value) => isVAT(value, 'IT') || 'VAT is not valid!'*/]
    },
    professionalLiabilityInsurance: {
      label: 'Professional liability insurance',
      value: undefined,
      rules: [requiredRule]
    },
    publicLiabilityInsurance: {
      label: 'Public liability insurance',
      value: undefined,
      rules: [requiredRule]
    },
    iban: {
      label: 'IBAN',
      value: '',
      rules: [requiredRule/*, (iban) => isIBAN(iban) || 'IBAN is not valid!'*/]
    },
    swift: {
      label: 'BIC/SWIFT',
      value: '',
      rules: [requiredRule/*, (bic) => isBIC(bic) || 'BIC is not valid!'*/]
    },
  }

  sections = {
    business: 'Business representative information',
    documents: 'Documents',
    company: 'Company information',
    payment: 'Payment information',
  }
  supportedFilesList = ['pdf', 'doc', 'docx', 'png', 'jpg', 'jpeg']
  supportedFilesHint: string = `Supported files: ${this.supportedFilesList.join(', ')}`

  // computed
  profile: IUser

  // watchers
  @Watch('initialValues')
  onInitialValuesChange (initialValues: InitialFormValues, prevInitialValues: InitialFormValues) {
    if (!initialValues) return
    this.setupInitialValues(initialValues)
  }

  created () {
    this.setupInitialValues(this.initialValues)
  }

  handleFileSelect (file: File) {
    this.formFields.passport.value = file
  }

  handleCancelClick () {
    this.setupInitialValues(this.initialValues)
    this.$emit('cancel')
  }

  handleSaveClick () {
    if (this.$refs.form.validate()) {
      const formData = Object.entries(this.formFields)
        .reduce((acc, [key, formItem]) => ({
          ...acc,
          [key]: formItem.value,
        }), {})

      this.$emit('submit', formData as FormValues)
    }
  }

  setupInitialValues (initialValues?: InitialFormValues) {
    if (!initialValues) return
    Object.entries(initialValues).forEach(([key, value]) => {
      if (fileFields.includes(key) && value) {
        this.formFields[key as FieldId].value = new File([''], getFileNameFromUrl(value))
        return
      }
      this.formFields[key as FieldId].value = value
    })
    this.$refs.form?.resetValidation()
  }
}
</script>
